// API helpers for the Bed Transfer Requisition workflow (G12). Tenant
// headers are sent explicitly on every call — see ipdEnhancementsApi.js.

import axios from "axios";

const BASE =
  process.env.REACT_APP_BACKEND_ROUTE_URL ||
  "https://manage-dev.hlthtek.com/app/api/";

function authHeaders(extra = {}) {
  const token = sessionStorage.getItem("accessToken") || "";
  return {
    Authorization: `Bearer ${token}`,
    "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
    "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
    "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
    USERNAME: sessionStorage.getItem("userName") || "",
    ...extra,
  };
}

// Executing a transfer also posts the bed-day charge it incurs (ADR 0008), and
// bills the retained bed too when the patient held their original one (ADR 0006).
// The bed has already physically moved by the time billing is called, so a
// billing failure comes back as bed_charge_warning on an otherwise successful
// response — staff must be told to post it by hand rather than assume it's done.
export function describeBedCharge(res) {
  if (res?.bed_charge_warning) {
    return { severity: "warning", text: res.bed_charge_warning };
  }
  const charge = res?.bed_charge;
  if (!charge) return null;

  const event = charge.charge_event || {};
  const amount = event.gross_amount;
  const label = [event.service_name, amount != null ? `₹${amount}` : null]
    .filter(Boolean)
    .join(" — ");
  let text = label ? `Bed charge posted: ${label}.` : "Bed charge posted.";

  const retention = charge.retention_charge;
  if (retention?.charge_event) {
    const retained = retention.charge_event.gross_amount;
    text += ` Retained bed also billed${retained != null ? ` (₹${retained})` : ""}.`;
  } else if (retention?.status === "Failed") {
    return {
      severity: "warning",
      text: `${text} The retained bed was NOT billed: ${retention.message}`,
    };
  }
  return { severity: "success", text };
}

export async function raiseTransferRequest({
  admissionId,
  fromBedCode,
  toBedCode,
  transferType,
  reason,
  newAdmittingDoctorId,
  initiatedBy,
  hospitalTransferReason,
  retainFromBed,
  retentionExpiresAt,
  retentionReason,
}) {
  const body = {
    admission_id: admissionId,
    from_bed_code: fromBedCode,
    to_bed_code: toBedCode,
    transfer_type: transferType || "within_ward",
    // "hospital" | "patient" — who asked for the transfer. Decides which of the
    // two beds the transfer bills for (ADR 0008).
    initiated_by: initiatedBy || "hospital",
  };
  // "clinical" | "convenience". Required by the backend on hospital-initiated
  // raises: a clinical escalation bills the destination bed, a move made for the
  // hospital's own convenience bills the cheaper one.
  if (hospitalTransferReason) body.hospital_transfer_reason = hospitalTransferReason;
  if (reason) body.reason = reason;
  // Optional next-attending-doctor handover (E6-BE) — omitted entirely when
  // unchosen so an unset selector raises the request exactly as before.
  if (newAdmittingDoctorId) body.new_admitting_doctor_id = newAdmittingDoctorId;
  // Hold the original bed for the patient's return instead of freeing it. The
  // backend requires retention_expires_at whenever this is set, and bills BOTH
  // beds for as long as the retention is active (ADR 0006). Omitted entirely
  // when off, so an ordinary transfer is raised exactly as before.
  if (retainFromBed) {
    body.retain_from_bed = true;
    body.retention_expires_at = retentionExpiresAt;
    if (retentionReason) body.retention_reason = retentionReason;
  }
  const res = await axios.post(`${BASE}bed_transfer_request/`, body, {
    headers: authHeaders({ "Content-Type": "application/json" }),
  });
  return res?.data;
}

export async function getTransferRequests({ status, department, admissionId } = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (department) params.set("department", department);
  if (admissionId) params.set("admission_id", admissionId);
  const qs = params.toString();
  const res = await axios.get(
    `${BASE}bed_transfer_request/${qs ? `?${qs}` : ""}`,
    { headers: authHeaders() }
  );
  return res?.data?.data || [];
}

export async function approveTransferRequest(id) {
  const res = await axios.put(
    `${BASE}bed_transfer_request/${id}/approve/`,
    {},
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return res?.data;
}

export async function rejectTransferRequest(id, rejectionReason) {
  const res = await axios.put(
    `${BASE}bed_transfer_request/${id}/reject/`,
    { rejection_reason: rejectionReason },
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return res?.data;
}

export async function cancelTransferRequest(id) {
  const res = await axios.put(
    `${BASE}bed_transfer_request/${id}/cancel/`,
    {},
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return res?.data;
}

export async function executeTransferRequest(id) {
  const res = await axios.post(
    `${BASE}bed_transfer_request/${id}/execute/`,
    {},
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return res?.data;
}

// ---- Patient search (requirement #3) --------------------------------------
// Used by RaiseTransferRequestModal's patient-search step when the caller
// doesn't already know the admission/bed (e.g. raising a request from the
// requisition list rather than the bed dashboard).

export async function searchPatients(query) {
  const res = await axios.get(
    `${BASE}get_patient_list_with_aabha_info/?search=${encodeURIComponent(query)}`,
    { headers: authHeaders() }
  );
  return res?.data?.data || [];
}

export async function getAllocatedAdmissionForPatient(patientId) {
  const res = await axios.get(
    `${BASE}ipd_admission/?status=Allocated&search=${encodeURIComponent(patientId)}`,
    { headers: authHeaders() }
  );
  return res?.data?.data || [];
}
